import { useState } from "react";
import { Plus, Trash2, MapPin, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import PTTButton from "./PTTButton";
import { IDS } from "../constants/testIds";

export default function RosterSidebar({ contacts, activeDirectId, txStatus, onPTTStart, onPTTEnd, onAdd, onDelete, onToggleOnline, selectMode, selectedIds = [], onToggleSelect, onCreateTalkgroup }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState("Field Unit");
  const [avatar, setAvatar] = useState("");
  const [groupMode, setGroupMode] = useState(false);
  const [groupSel, setGroupSel] = useState([]);
  const [tgOpen, setTgOpen] = useState(false);
  const [tgName, setTgName] = useState("");

  const submit = async () => {
    if (!name.trim()) return;
    await onAdd({ name: name.trim(), role, avatar_url: avatar || undefined });
    setName(""); setRole("Field Unit"); setAvatar(""); setOpen(false);
  };
  const toggleGroupSel = (id) => setGroupSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const submitTalkgroup = async () => {
    if (!tgName.trim() || groupSel.length === 0) return;
    await onCreateTalkgroup({ name: tgName.trim(), member_ids: groupSel });
    setTgName(""); setGroupSel([]); setTgOpen(false); setGroupMode(false);
  };
  const picking = selectMode || groupMode;

  return (
    <aside data-testid={IDS.roster.root} className="h-full flex flex-col gap-3 min-w-0">
      <div className="flex items-center justify-between flex-shrink-0 gap-2">
        <div className="min-w-0">
          <div className="overline">Roster · {contacts.filter(c => c.online).length}/{contacts.length}</div>
          <h2 className="text-xl font-semibold tracking-tight">Field Units</h2>
        </div>
        <div className="flex items-center gap-1.5">
        <Button data-testid={IDS.talkgroups.groupMode} size="sm" variant={groupMode ? "default" : "outline"} className={`gap-1.5 ${groupMode ? "bg-teal-600 hover:bg-teal-700" : ""}`} onClick={() => { setGroupMode(m => !m); setGroupSel([]); }} title="Select units for a talkgroup" disabled={selectMode}>
          <Users className="h-4 w-4" />{groupMode && <span className="font-mono text-[10px]">{groupSel.length}</span>}
        </Button>
        {groupMode && (
          <Dialog open={tgOpen} onOpenChange={setTgOpen}>
            <DialogTrigger asChild>
              <button data-testid={IDS.talkgroups.create} disabled={groupSel.length === 0} className="h-9 w-9 rounded-full grid place-items-center bg-teal-600 text-white shadow hover:bg-teal-700 disabled:opacity-40 transition" aria-label="Create talkgroup from selected units" title="Create talkgroup">
                <Plus className="h-5 w-5" />
              </button>
            </DialogTrigger>
            <DialogContent data-testid={IDS.talkgroups.dialog} className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>New talkgroup</DialogTitle>
                <DialogDescription>Private ad-hoc group of {groupSel.length} unit{groupSel.length !== 1 ? "s" : ""}. Tear it down anytime.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-1.5 py-2">
                <Label>Talkgroup name</Label>
                <Input data-testid={IDS.talkgroups.name} value={tgName} onChange={e => setTgName(e.target.value)} placeholder="Tac 1 — North Gate" onKeyDown={e => e.key === "Enter" && submitTalkgroup()} />
                <div className="flex flex-wrap gap-1 mt-1">
                  {groupSel.map(id => contacts.find(c => c.id === id)).filter(Boolean).map(c => <span key={c.id} className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50">{c.name}</span>)}
                </div>
              </div>
              <DialogFooter>
                <Button data-testid={IDS.talkgroups.submit} onClick={submitTalkgroup} disabled={!tgName.trim()}>Create talkgroup</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={IDS.roster.addUser} size="sm" variant="outline" className="gap-1.5">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent data-testid={IDS.roster.addUserDialog} className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add field unit</DialogTitle>
              <DialogDescription>Add a new user to your dispatch roster.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <Label>Name</Label>
                <Input data-testid={IDS.roster.addUserName} value={name} onChange={e => setName(e.target.value)} placeholder="Alex Rivera" />
              </div>
              <div className="grid gap-1.5">
                <Label>Role</Label>
                <Input data-testid={IDS.roster.addUserRole} value={role} onChange={e => setRole(e.target.value)} placeholder="Field Unit" />
              </div>
              <div className="grid gap-1.5">
                <Label>Avatar URL (optional)</Label>
                <Input value={avatar} onChange={e => setAvatar(e.target.value)} placeholder="https://…" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid={IDS.roster.addUserSubmit} onClick={submit}>Add unit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {groupMode && <p className="text-[11px] text-teal-700 bg-teal-50 border border-teal-200 rounded-md px-2 py-1 -mt-1">Tick units, then press <b>+</b> to create a talkgroup. You can also drag an avatar onto an existing talkgroup.</p>}

      <div className="flex-1 min-h-0 rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto thin-scroll divide-y divide-slate-100">
          {contacts.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">No units. Add one to get started.</div>
          )}
          {contacts.map(ct => {
            const isActive = activeDirectId === ct.id;
            const status = isActive ? txStatus : "idle";
            const selected = selectMode ? selectedIds.includes(ct.id) : groupSel.includes(ct.id);
            const toggle = selectMode ? onToggleSelect : toggleGroupSel;
            return (
              <div key={ct.id} data-testid={IDS.roster.item(ct.id)} onClick={picking ? () => toggle(ct.id) : undefined} className={`p-3 flex items-center gap-3 transition group ${selected ? (selectMode ? "bg-amber-50" : "bg-teal-50") : "hover:bg-slate-50"} ${picking ? "cursor-pointer" : ""}`}>
                {picking && (
                  <Checkbox
                    data-testid={selectMode ? IDS.broadcast.contactCheck(ct.id) : IDS.talkgroups.check(ct.id)}
                    checked={selected}
                    onCheckedChange={() => toggle(ct.id)}
                    onClick={e => e.stopPropagation()}
                    className={`h-5 w-5 ${selectMode ? "data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" : "data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"}`}
                  />
                )}
                <div
                  className="relative cursor-grab active:cursor-grabbing"
                  data-testid={IDS.talkgroups.avatar(ct.id)}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData("text/contact-id", ct.id); e.dataTransfer.effectAllowed = "copy"; }}
                  title="Drag onto a talkgroup to add"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={ct.avatar_url} alt={ct.name} />
                    <AvatarFallback>{ct.name.split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback>
                  </Avatar>
                  <button
                    data-testid={IDS.roster.online(ct.id)}
                    onClick={(e) => { e.stopPropagation(); onToggleOnline(ct.id); }}
                    className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-0.5"
                    aria-label="Toggle online status"
                  >
                    <span className={`dot ${ct.online ? "online" : "offline"}`} />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{ct.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                    {ct.lat != null && <MapPin className="h-3 w-3" />}
                    <span className="truncate">{ct.role || "Field Unit"}</span>
                  </div>
                </div>
                <span onClick={e => e.stopPropagation()}>
                <PTTButton
                  testId={IDS.roster.ptt(ct.id)}
                  label={`Direct PTT to ${ct.name}`}
                  color={status === "transmitting" ? "green" : "slate"}
                  status={status}
                  size="sm"
                  disabled={!ct.online}
                  onPressStart={() => onPTTStart(ct)}
                  onPressEnd={() => onPTTEnd(ct)}
                />
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(ct.id); }}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition"
                  aria-label="Remove unit"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
